<template>
  <form :class="$style.container">
    <p :class="$style.text">
      {{ config.text }}
    </p>
    <div :class="[$style.checkboxes, 'columns is-mobile is-multiline']">
      <div
        v-for="(checkbox, index) in config.newsletters"
        :key="`checkbox-${index}`"
        class="column is-half"
      >
        <input
          :id="`checkbox-${index}`"
          v-model="newsletterIds"
          :name="`checkbox-${index}`"
          type="checkbox"
          :value="checkbox.newsletterId"
        />
        <label :for="`checkbox-${index}`">{{ checkbox.label }}</label>
      </div>
    </div>
    <div v-if="checkboxError" :class="$style.serverError">
      {{ config.checkboxErrorMessage }}
    </div>
    <div class="columns">
      <div class="column is-half-tablet">
        <FormInput
          v-model.trim="v$.formData.email.$model"
          :bold-label="true"
          :error="v$.formData.email.$errors.length && v$.$dirty && isSubmitted"
          :error-message="config.labels.email.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.email.label"
          :original-value="
            previousData.email && previousData.email !== '' ? previousData.email : null
          "
        />
      </div>
    </div>
    <p :class="$style.title">
      {{ config.optionalSectionTitle }}
    </p>
    <p :class="$style.text">
      {{ config.optionalSectionText }}
    </p>
    <div class="columns">
      <div class="column">
        <FormSelect
          v-model="formData.anrede"
          :error="false"
          :initial-option="config.titleInitialSelectOption"
          :label="config.labels.title.label"
          :optional-label="config.labels.title.optionLabel"
          :options="config.titleSelectOptions"
        />
      </div>
      <div class="column">
        <FormSelect
          v-model="formData.land"
          :error="false"
          :initial-option="config.initialSelectOption"
          :label="config.labels.country.label"
          :optional-label="config.labels.country.optionLabel"
          :options="config.selectOptions"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <FormInput
          v-model="formData.vorname"
          :bold-label="true"
          :error="false"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.firstName.label"
          :optional-label="config.labels.firstName.optionLabel"
          :original-value="
            previousData.firstName && previousData.firstName !== '' ? previousData.firstName : null
          "
        />
      </div>
      <div class="column">
        <FormInput
          v-model="formData.name"
          :bold-label="true"
          :error="false"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.lastName.label"
          :optional-label="config.labels.lastName.optionLabel"
          :original-value="
            previousData.lastName && previousData.lastName !== '' ? previousData.lastName : null
          "
        />
      </div>
    </div>
    <div :class="['columns', $style.dataSecurityColumn]">
      <div :class="['column', $style.dataSecurityChecked]">
        <input
          :id="`checkbox-dataSecurity`"
          v-model.trim="formData.dataSecurity"
          :class="v$.formData.dataSecurity.$errors.length && v$.$dirty && isSubmitted && 'error'"
          :name="`checkbox-dataSecurity`"
          type="checkbox"
        />
        <label :for="`checkbox-dataSecurity`" v-html="config.dataSecurityText" />
        <div
          v-if="v$.formData.dataSecurity.$errors.length && v$.$dirty && isSubmitted"
          :class="$style.dataSecurityError"
          v-html="config.dataSecurityErrorMessage"
        />
      </div>
    </div>
    <div v-if="serverError" :class="$style.serverError">
      {{ config.serverErrorMessage }}
    </div>
    <div v-if="serverSuccess" :class="$style.serverSuccess">
      <p>{{ config.subscribeSuccess }}</p>
      <p>{{ config.confirmationHint }}</p>
      <p>{{ config.spamFilterHint }}</p>
    </div>
    <div class="columns">
      <div class="column has-text-right-tablet">
        <Button
          colour="blue"
          :is-form="true"
          :text="config.buttonText"
          type="submit"
          @close="handleSubmit"
        />
      </div>
    </div>
    <ReCaptcha :execute-switch="executeSwitch" :submit="submit" />
  </form>
</template>

<script setup>
import { required, email as emailRule } from '@vuelidate/validators';
import ReCaptcha from '../ReCaptcha/ReCaptcha.vue';
import FormInput from '../FormInput/FormInput.vue';
import FormSelect from '../FormSelect/FormSelect.vue';
import Button from '../Button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import { onMounted, ref } from 'vue';
import safeFetch from '../../utils/safeFetch';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

// form fields
const formData = ref({
  newsletter: '',
  vorname: '',
  name: '',
  anrede: '',
  land: '',
  email: 'test',
  domain: '',
  pageUrl: '',
  token: '',
  dataSecurity: false,
});

// regular refs
const newsletterIds = ref([]);
const previousData = ref({ ...formData.value });
const isSubmitted = ref(false);
const formSubmitSwitch = ref(false);
const executeSwitch = ref(false);
const serverError = ref(false);
const serverSuccess = ref(false);
const checkboxError = ref(false);

const shouldBeTrue = (val) => {
  return val === true;
};

const rules = {
  formData: {
    email: {
      required,
      emailRule,
    },
    dataSecurity: {
      shouldBeTrue,
    },
  },
};
const v$ = useVuelidate(rules, { formData });

onMounted(() => {
  if (props.config.initialSelectOption) {
    formData.value.land = props.config.initialSelectOption;
  }
  if (props.config.titleInitialSelectOption) {
    formData.value.anrede = props.config.titleInitialSelectOption;
  }
  const queries = window.location.search.substring(1).split('&');
  if (queries) {
    for (const query of queries) {
      if (query.includes('email')) {
        formData.value.email = query.split('=')[1];
      }
    }
  }
});

const handleSubmit = () => {
  serverError.value = false;
  serverSuccess.value = false;
  checkboxError.value = false;
  v$.value.formData.email.$touch();
  v$.value.formData.dataSecurity.$touch();
  isSubmitted.value = true;
  formSubmitSwitch.value = !formSubmitSwitch.value;
  if (!v$.$invalid) {
    if (newsletterIds.value.length !== 0) {
      executeSwitch.value = !executeSwitch.value;
    } else {
      checkboxError.value = true;
    }
  } else {
    previousData.value = { ...formData.value };
  }
};

const submit = (newToken) => {
  formData.value.token = newToken;
  formData.value.domain = window.location.origin;
  formData.value.pageUrl = window.location.pathname;
  let combinedRequests = [];
  for (const id of newsletterIds.value) {
    formData.value.newsletter = id;
    combinedRequests.push(
      safeFetch(window.newsletterSubscribeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData.value }),
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response;
      }),
    );
  }
  Promise.all(combinedRequests)
    .then((responses) => {
      if (responses.every((response) => response.status === 200)) {
        serverSuccess.value = true;
      } else {
        serverSuccess.value = false;
        serverError.value = true;
      }
    })
    .catch((errors) => {
      serverSuccess.value = false;
      serverError.value = true;
      console.error(errors);
    });
};
</script>

<style module lang="scss">
@forward './NewsletterForm.scss';
</style>
